import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  first,
  map,
  shareReplay,
  skipWhile,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs/operators';
import { GroupPermission } from '../../admin/model/permission';
import { PermissionsTableService } from '../../admin/services/permissions-table.service';
import { User } from '../model/user';
import { AuthService, UserSession } from '../services/auth.service';
import { UsersService } from '../services/users.service';
import {
  AUTH_ROLES,
  AUTH_PERMISSIONS,
  JWT_CUSTOM_ATTRIBUTES,
} from '../consts/auth-roles.consts';
import { RoleUtils } from '../../../utils/role.utils';

export type GroupsWithPermission = {
  permission: string;
};

@Injectable()
export class RestrictIfService {
  _cachedPermissions = new Set<string>();

  _cachedRoles = new Set<string>();

  _loadPermissions: Observable<boolean>;

  constructor(
    private permissionsService: PermissionsTableService,
    private authService: AuthService,
    private usersService: UsersService
  ) {
    this._loadPermissions = this.authService.onUserSession.pipe(
      filter((userInfo) => !!userInfo?.isValid?.()),
      distinctUntilChanged((prev, curr) => 
        prev?.accessToken?.payload?.username === curr?.accessToken?.payload?.username
      ),
      switchMap((userInfo: any) => {
        const username = userInfo.accessToken.payload.username;
        
        this._cachedPermissions.clear();
        this._cachedRoles.clear();

        return this.usersService.user(username).pipe(
          timeout(5000),
          catchError((error) => {
            console.error('Error loading user for permissions:', error);
            const customGroups =
              userInfo.idToken.payload['custom:group']?.split('|') || [];
            return of({ role: customGroups } as User);
          })
        );
      }),
      map((user: User) => {
        return user.role?.length ? [...user.role] : [];
      }),
      tap((groups: string[]) => {
        groups.forEach((role) => this._cachedRoles.add(role));
      }),
      switchMap((groups: string[]): Observable<boolean> => {
        if (this._cachedPermissions.size) {
          return of(true);
        }
        return this.permissionsService.getGroupsWithPermissions().pipe(
          first(),
          timeout(5000),
          tap((permissions: GroupPermission[]) => {
            const usersPermissionsByGroup = permissions.filter((permission) =>
              groups.includes(permission.groupKey)
            );
            usersPermissionsByGroup.forEach((permission) => {
              this._cachedPermissions.add(permission.permissionKey);
            });
          }),
          map((): boolean => true),
          catchError((error): Observable<boolean> => {
            console.error('Error loading permissions:', error);
            return of(true);
          })
        );
      }),
      map((): boolean => true)
    ) as Observable<boolean>;
  }

  restrictTo(...permissions: string[]): Observable<boolean> {
    return this._loadPermissions.pipe(
      first(),
      switchMap((): Observable<boolean> => {
        for (const permission of permissions) {
          if (this._cachedPermissions.has(permission)) {
            return of(true);
          }
        }
        return of(false);
      })
    );
  }

  hasRoles(...roles: string[]): Observable<boolean> {
    return this._loadPermissions.pipe(
      switchMap(() => {
        for (const role of roles) {
          if (this._cachedRoles.has(role)) {
            return of(true);
          }
        }

        return of(false);
      })
    );
  }

  permissionsLoaded(): Observable<boolean> {
    return this._loadPermissions;
  }

  canManageTenant(tenantId: string): Observable<boolean> {
    return this._loadPermissions.pipe(
      switchMap(() => this.authService.onUserSession),
      map((session: UserSession) => {
        if (!session?.isValid()) return false;

        const groups =
          session.idToken.payload[JWT_CUSTOM_ATTRIBUTES.GROUP]?.split('|') ||
          [];

        if (groups.includes(AUTH_ROLES.UM_ADMINISTRATOR)) return true;

        const sacRoles = groups.filter((role) => RoleUtils.isSACRole(role));
        return sacRoles.some(
          (role) => RoleUtils.extractTenantIdFromRole(role) === tenantId
        );
      })
    );
  }

  canManageUsers(): Observable<boolean> {
    return this.restrictTo(
      AUTH_PERMISSIONS.ADD_NEW_USER,
      AUTH_PERMISSIONS.EDIT_USER_ROLES
    );
  }

  isGlobalAdmin(): Observable<boolean> {
    return this.hasRoles(AUTH_ROLES.UM_ADMINISTRATOR);
  }

  isSAC(): Observable<boolean> {
    return this._loadPermissions.pipe(
      switchMap(() => this.authService.onUserSession),
      map((session: UserSession) => {
        if (!session?.isValid()) return false;

        const groups =
          session.idToken.payload[JWT_CUSTOM_ATTRIBUTES.GROUP]?.split('|') ||
          [];
        return groups.some((role) => RoleUtils.isSACRole(role));
      })
    );
  }

  getSACTenantId(): Observable<string | null> {
    return this._loadPermissions.pipe(
      switchMap(() => this.authService.onUserSession),
      map((session: UserSession) => {
        if (!session?.isValid()) return null;

        const groups =
          session.idToken.payload[JWT_CUSTOM_ATTRIBUTES.GROUP]?.split('|') ||
          [];
        const sacRoles = groups.filter((role) => RoleUtils.isSACRole(role));
        return sacRoles.length > 0
          ? RoleUtils.extractTenantIdFromRole(sacRoles[0])
          : null;
      })
    );
  }

  canManageUser(targetUser: User): Observable<boolean> {
    return this._loadPermissions.pipe(
      switchMap(() => this.authService.onUserSession),
      map((session: UserSession) => {
        if (!session?.isValid()) return false;

        const groups =
          session.idToken.payload[JWT_CUSTOM_ATTRIBUTES.GROUP]?.split('|') ||
          [];

        if (groups.includes(AUTH_ROLES.UM_ADMINISTRATOR)) return true;

        const sacRoles = groups.filter((role) => RoleUtils.isSACRole(role));
        if (sacRoles.length > 0) {
          const managedTenantId = RoleUtils.extractTenantIdFromRole(
            sacRoles[0]
          );
          const normalizedManagedTenantId = RoleUtils.normalizeTenantId(managedTenantId);
          const normalizedUserTenantIds = (targetUser.tenantIDs || []).map(id => RoleUtils.normalizeTenantId(id));
          return normalizedUserTenantIds.includes(normalizedManagedTenantId) || false;
        }

        return false;
      })
    );
  }
}
